// Lightweight word-level n-gram LM built on-the-fly from a passage word list.
//
// Used during CTC beam search to score P(word | recent context) at word
// boundaries only — no per-token overhead. Counts are frozen at session
// start; scoring is a few Map lookups per completed word.

const DEFAULT_ORDER = 2;
const DEFAULT_WEIGHT = 0.5;
const DEFAULT_SMOOTHING = 0.1;
const MAX_CONTEXT = 2;

function assignWordIds(words) {
  const wordToId = new Map();
  for (const w of words) {
    const wl = w.toLowerCase();
    if (!wordToId.has(wl)) wordToId.set(wl, wordToId.size);
  }
  return wordToId;
}

function pack2(a, b) {
  return (a << 16) | b;
}

function pack3(a, b, c) {
  return ((a << 16) | b) * 65536 + c;
}

export class NgramLm {
  constructor(order = DEFAULT_ORDER, smoothing = DEFAULT_SMOOTHING, weight = DEFAULT_WEIGHT) {
    if (order < 1 || order > 3) throw new Error(`ngram order must be 1–3, got ${order}`);
    this.order = order;
    this.smoothing = smoothing;
    this.weight = weight;
    this.wordToId = null;
    this.unigram = new Map();
    this.bigram = new Map();
    this.trigram = new Map();
    this.total = 0;
    this.vocabSize = 0;
    this._contextLen = Math.min(order - 1, MAX_CONTEXT);
  }

  // Build counts from passage word sequence (may contain repeats).
  static fromWordList(wordList, opts = {}) {
    const order = opts.order ?? DEFAULT_ORDER;
    const smoothing = opts.smoothing ?? DEFAULT_SMOOTHING;
    const weight = opts.weight ?? DEFAULT_WEIGHT;
    const lm = new NgramLm(order, smoothing, weight);
    const words = wordList.map((w) => w.toLowerCase());
    lm.wordToId = assignWordIds(words);
    lm.vocabSize = lm.wordToId.size;

    for (let i = 0; i < words.length; i++) {
      const id = lm.wordToId.get(words[i]);
      lm._incUnigram(id);
      if (i > 0) {
        const prev = lm.wordToId.get(words[i - 1]);
        lm._incBigram(prev, id);
      }
      if (i > 1) {
        const w2 = lm.wordToId.get(words[i - 2]);
        const w1 = lm.wordToId.get(words[i - 1]);
        lm._incTrigram(w2, w1, id);
      }
    }
    return lm;
  }

  _incUnigram(id) {
    this.unigram.set(id, (this.unigram.get(id) ?? 0) + 1);
    this.total++;
  }

  _incBigram(prev, curr) {
    this.bigram.set(pack2(prev, curr), (this.bigram.get(pack2(prev, curr)) ?? 0) + 1);
  }

  _incTrigram(w2, w1, curr) {
    this.trigram.set(pack3(w2, w1, curr), (this.trigram.get(pack3(w2, w1, curr)) ?? 0) + 1);
  }

  // Log-domain score scaled by weight. history is Uint16Array; historyLen ≤ 2.
  score(history, historyLen, wordId) {
    if (wordId < 0) return 0;
    const k = this.smoothing;
    const V = this.vocabSize;
    let logP;

    if (this.order >= 3 && historyLen >= 2) {
      const w2 = history[historyLen - 2];
      const w1 = history[historyLen - 1];
      const ctx = this.bigram.get(pack2(w2, w1)) ?? 0;
      const count = this.trigram.get(pack3(w2, w1, wordId)) ?? 0;
      if (ctx > 0) {
        logP = Math.log((count + k) / (ctx + k * V));
        return this.weight * logP;
      }
    }

    if (this.order >= 2 && historyLen >= 1) {
      const prev = history[historyLen - 1];
      const ctx = this.unigram.get(prev) ?? 0;
      const count = this.bigram.get(pack2(prev, wordId)) ?? 0;
      if (ctx > 0) {
        logP = Math.log((count + k) / (ctx + k * V));
        return this.weight * logP;
      }
    }

    const count = this.unigram.get(wordId) ?? 0;
    logP = Math.log((count + k) / (this.total + k * V));
    return this.weight * logP;
  }
}

export function pushWordHistory(beam, wordId, contextLen) {
  const h = beam.wordHistory;
  const len = beam.wordHistoryLen;
  if (len < contextLen) {
    h[len] = wordId;
    beam.wordHistoryLen = len + 1;
  } else if (contextLen === 2) {
    h[0] = h[1];
    h[1] = wordId;
  } else if (contextLen === 1) {
    h[0] = wordId;
  }
}
